import { useMemo } from "react";
import { ShareLinkFile } from "../entities";
import { pathSegments } from "../utils/fileFormat";

/**
 * A directory node in the space tree, built from the flat file list. `path` is
 * the list of folder names from the root to this node (the root itself is `[]`).
 */
export type SpaceDir = {
  name: string;
  path: string[];
  /** UID of the folder's directory entry, used to address it in a deep link. */
  uid?: string;
  /** Immediate subdirectories, keyed by folder name. */
  dirs: Map<string, SpaceDir>;
  /** Files that live directly in this directory (not in a subfolder). */
  files: ShareLinkFile[];
};

/** A subfolder as surfaced to the UI (rail card / breadcrumb / sheet tile). */
export type SpaceFolderEntry = {
  name: string;
  /** Full path from the root, so tapping can navigate straight to it. */
  path: string[];
  /** UID of the folder's directory entry (for `?target=` deep links). */
  uid?: string;
  /** Immediate children (subfolders + files). */
  itemCount: number;
  /** Cumulative byte size of every file in the folder, including deeply nested ones. */
  size: number;
  /**
   * Most recent modification time across every file in the folder (deeply
   * nested included) — the folder's effective "last modified". `null` when the
   * folder holds no dated files.
   */
  modifiedAt: Date | null;
};

/** The immediate contents of one directory, split folders-first per the design. */
export type SpaceDirContents = {
  folders: SpaceFolderEntry[];
  files: ShareLinkFile[];
  /** False when the requested path no longer resolves (e.g. a stale deep link). */
  exists: boolean;
};

/** MIME type the API uses for directory entries in a space's flat file list. */
const DIRECTORY_MIME = "inode/directory";

/**
 * Builds the directory tree from a space's flat entry list. The list interleaves
 * files with explicit directory entries (`inode/directory`): a directory entry
 * creates the folder node (so even empty folders appear) and stamps it with the
 * entry's `uid`, while files are placed in the folder named by all-but-their-last
 * path segment. Missing intermediate folders are created on demand, so the tree
 * is correct with or without explicit directory entries (an implicit folder just
 * has no `uid`).
 */
export function buildSpaceTree(entries: ShareLinkFile[]): SpaceDir {
  const root: SpaceDir = { name: "", path: [], dirs: new Map(), files: [] };
  const getOrCreate = (dir: SpaceDir, name: string): SpaceDir => {
    let child = dir.dirs.get(name);
    if (!child) {
      child = { name, path: [...dir.path, name], dirs: new Map(), files: [] };
      dir.dirs.set(name, child);
    }
    return child;
  };
  for (const entry of entries) {
    const segments = pathSegments(entry.path);
    if (entry.mimeType === DIRECTORY_MIME) {
      // The whole path is a folder chain; the entry itself is not a file. Stamp
      // the leaf node with the directory entry's uid (its deep-link address).
      const dir = segments.reduce(getOrCreate, root);
      if (dir !== root) dir.uid = entry.uid;
      continue;
    }
    // A file: all but the final segment is its folder chain.
    const dir = segments.slice(0, -1).reduce(getOrCreate, root);
    dir.files.push(entry);
  }
  return root;
}

/** Walks `path` from `root`, returning the directory or `undefined` if absent. */
export function resolveDir(
  root: SpaceDir,
  path: string[]
): SpaceDir | undefined {
  let dir: SpaceDir | undefined = root;
  for (const name of path) {
    dir = dir.dirs.get(name);
    if (!dir) return undefined;
  }
  return dir;
}

/** Cumulative byte size of every file in `dir`, recursing into subfolders. */
function dirTotalSize(dir: SpaceDir): number {
  let total = dir.files.reduce((sum, f) => sum + (f.size ?? 0), 0);
  for (const child of dir.dirs.values()) total += dirTotalSize(child);
  return total;
}

/** Latest file modification time (epoch ms) anywhere in `dir`; 0 if none. */
function dirLatestModifiedMs(dir: SpaceDir): number {
  let latest = 0;
  for (const f of dir.files) {
    if (!f.modifiedAt) continue;
    const t = new Date(f.modifiedAt).getTime();
    if (!Number.isNaN(t)) latest = Math.max(latest, t);
  }
  for (const child of dir.dirs.values()) {
    latest = Math.max(latest, dirLatestModifiedMs(child));
  }
  return latest;
}

/**
 * Immediate contents of a directory: subfolders then files. Folder order is left
 * to the caller — the desktop view sorts them with its own locale comparator
 * (honoring the active sort direction), so ordering here would just be wasted,
 * subtly-different work.
 */
function dirContents(dir: SpaceDir): SpaceDirContents {
  const folders: SpaceFolderEntry[] = [...dir.dirs.values()].map((d) => {
    const latest = dirLatestModifiedMs(d);
    return {
      name: d.name,
      path: d.path,
      uid: d.uid,
      itemCount: d.dirs.size + d.files.length,
      size: dirTotalSize(d),
      modifiedAt: latest > 0 ? new Date(latest) : null,
    };
  });
  return { folders, files: dir.files, exists: true };
}

/**
 * Resolves the contents of the directory at `path` within the space tree built
 * from `files`. The tree is memoized per file list; the contents recompute when
 * the current path changes. Returns `{ exists: false }` when the path no longer
 * resolves (e.g. after an unpublish or a stale deep link) so the caller can fall
 * back to the root.
 */
export function useSpaceTree(
  files: ShareLinkFile[],
  path: string[]
): SpaceDirContents {
  const root = useMemo(() => buildSpaceTree(files), [files]);
  return useMemo(() => {
    const dir = resolveDir(root, path);
    if (!dir) return { folders: [], files: [], exists: false };
    return dirContents(dir);
  }, [root, path]);
}
